import type { Dayjs } from 'dayjs';
import type { RouteLocationRaw } from '#vue-router';
import type { SpecialOfferData } from '@/types/generated';
import {
  ADVERT_TYPE_BTR,
  ADVERT_TYPE_HMO,
} from '~/modules/properties/constants';

export type PropertyAddEditAction = 'create' | 'edit';

export type AdvertType = typeof ADVERT_TYPE_HMO | typeof ADVERT_TYPE_BTR;

export enum PropertyActionType {
  MAKE_LIVE = 'makeLive',
  MAKE_NOT_LIVE = 'makeNotLive',
  BOOST = 'boost',
  FEATURE = 'feature',
  SET_DATES = 'setDates',
  DELETE = 'delete',
}

export enum PropertyRoomsActionType {
  DELETE = 'delete',
}

export enum PropertyNotLiveReason {
  LET = 'let',
  LET_VIA_UNIHOMES = 'letViaUnihomes',
  LET_OTHER_AGENT = 'letOtherAgent',
  SOLD = 'sold',
  REMOVED_REMARKETING_LATER = 'removedRemarketingLater',
  OTHER = 'other',
}

/**
 * Using locally defined BedroomType due to Nuxt error in modules folder:
 * ReferenceError: App is not defined (App.Domain.Properties.Enums.RoomType)
 */
export enum BedroomType {
  'SINGLE' = 1,
  'DOUBLE' = 2,
  'SINGLE_ENSUITE' = 3,
  'DOUBLE_ENSUITE' = 4,
  'ALREADY_LET' = 0,
}

export type BedroomData = {
  id: number;
  type: BedroomType;
  isLet: boolean;
  price?: number;
  unit_is_live?: boolean;
};

export type GoogleMapsPlace = {
  address_components: [];
  adr_address: string;
  business_status: string;
  current_opening_hours: {
    open_now: boolean;
    periods: {}[];
    weekday_text: string[];
  };
  formatted_address: string;
  formatted_phone_number: string;
  geometry: {
    location: unknown;
    viewport: unknown;
  };
  html_attributions: [];
  icon: string;
  icon_background_color: string;
  icon_mask_base_uri: string;
  international_phone_number: string;
  name: string;
  opening_hours: {
    periods: {}[];
    weekday_text: string[];
    isOpen: unknown;
    open_now: boolean;
  };
  photos?: unknown[];
  place_id: string;
  plus_code: {
    compound_code: string;
    global_code: string;
  };
  price_level: number;
  rating: number;
  reference: string;
  reviews?: {
    author_name: string;
    author_url: string;
    language: string;
    profile_photo_url: string;
    rating: number;
    relative_time_description: string;
    text: string;
    time: number;
  }[];
  types: string[];
  url: string;
  user_ratings_total: number;
  utc_offset: number;
  utc_offset_minutes: number;
  vicinity: string;
  website: string;
};

export type LoqateAddress = {
  AdminAreaCode: string;
  AdminAreaName: string; // 'Sheffield';
  Barcode: string; // '(S117GE1QH)';
  Block: string;
  BuildingName: string;
  BuildingNumber: string;
  City: string;
  Company: string;
  CountryIso2: string; // 'GB';
  CountryIso3: string; // 'GBR';
  CountryIsoNumber: number; // 826;
  CountryName: string; // 'United Kingdom';
  DataLevel: string; // 'Premise';
  Department: string;
  District: string;
  DomesticId: string; // '20653218';
  Field1: string; // Latitude;
  Field2: string; // Longitude;
  Field3: string;
  Field4: string;
  Field5: string;
  Field6: string;
  Field7: string;
  Field8: string;
  Field9: string;
  Field10: string;
  Field11: string;
  Field12: string;
  Field13: string;
  Field14: string;
  Field15: string;
  Field16: string;
  Field17: string;
  Field18: string;
  Field19: string;
  Field20: string;
  FormattedLine1: string;
  FormattedLine2: string;
  FormattedLine3: string;
  FormattedLine4: string;
  FormattedLine5: string;
  Id: string; // 'GB|RM|B|20653218';
  Label: string; // 'HOUSE NUMBER STREET\CITY\nPOSTCODE\nCOUNTRY';
  Language: string; // 'ENG';
  LanguageAlternatives: string; // 'ENG';
  Line1: string; // 'HOUSE NUMBER STREET';
  Line2: string;
  Line3: string;
  Line4: string;
  Line5: string;
  Neighbourhood: string;
  POBoxNumber: string;
  PostalCode: string;
  Province: string; // County;
  ProvinceCode: string;
  ProvinceName: string; // County;
  SecondaryStreet: string;
  SortingNumber1: string; // '40153';
  SortingNumber2: string;
  Street: string;
  SubBuilding: string;
  Type: string; // 'Residential';
};

export type BoostPropertyResponse = {
  id: number;
  boosts: number;
  message: string;
};

export interface BedRoom {
  id: number;
  isLet: boolean;
  number: number;
  price: number | null;
  propertyRoomFeedReferenceId?: number | null;
  propertyRoomFeedReferenceCreatedAt?: string | null;
  seasonal_enquiry_count: number;
  totalPrice: string | number | null;
  type: BedroomType;
  unit_amenities: Array<any>;
  unit_available_from: string | null;
  unit_available_to: string | null;
  unit_bathrooms: number | null;
  unit_bedrooms: number | null;
  unit_count: number | null;
  unit_description: string | null;
  unit_holding_deposit: string | null;
  unit_is_live: boolean;
  unit_kitchen: App.Domain.Properties.Enums.KitchenType | null;
  unit_media: Array<Domain.Properties.DataObjects.PropertyForm.MediaData>;
  unit_name: string | null;
  unit_ref: string | null;
  unit_security_deposit: string | null;
  unit_type: App.Domain.Properties.Enums.UnitType | null;
}

export interface PropertyImage {
  id: number;
  deletedAt?: null | string;
  file?: null | File;
  hiddenAt?: null | string;
  mediumUrl?: string | null;
  message?: string;
  name?: string;
  position?: number;
  progress?: number;
  propertyId?: number;
  smallUrl?: string;
  status?: string;
  storedName?: string;
  tabletUrl?: string;
  url?: string;
}

export interface PropertyYoutube {
  id: string | number;
  youtube_id: string;
  original_url: string;
}

export interface PropertyWalkthrough {
  id: string | number;
  original_url: string;
}

export interface PropertyFloorplan {
  id: number;
  status: string;
  message?: string;
}

export interface SelectedUniversities {
  id: number;
}

export interface PropertyAmenity {
  added?: number;
  address: {
    postal_town: string;
    postcode: string;
    street: string;
    street_no: string;
  };
  addressString?: string;
  amenity_id?: number | null;
  business_status?: string | null;
  distance?: string | number | null;
  id: string | number;
  image_url?: string | null;
  images?: string[];
  latitude: number;
  longitude: number;
  name: string | null;
  place_id?: string | null;
  price_level?: string | number | null;
  rating: number | null;
  times: {
    WALKING?: number | null;
    BICYCLING?: number | null;
    DRIVING?: number | null;
  };
  user_ratings_total?: number | null;
  website?: string | null;
  types?: string[];
}

export interface PropertyForm {
  advert_type: AdvertType;
  api_property_id?: string;
  api_property_name?: string;
  api_integration_id?: number;
  is_feed_property: boolean;
  postcode: string;
  house_no: string;
  street: string;
  longitude: number | null;
  latitude: number | null;
  area: string | number;
  type: 'house' | 'apartment' | 'studio' | '';
  partial_let: boolean;
  epc: string;
  made_live_at: string | null;
  available_from: Dayjs | null;
  available_to: Dayjs | null;
  rooms: BedRoom[];
  images: PropertyImage[];
  youtube: PropertyYoutube[];
  walkthroughs: PropertyWalkthrough[];
  floorplans: PropertyFloorplan[];
  features: string[];
  specialOffers: SpecialOfferData[];
  selectedUniversities: SelectedUniversities[];
  amenities: PropertyAmenity[];
  room_count: number;
  bathroom_count: number;
  city_id: number | null;
  deposit: string | number;
  holding_deposit: string | number;
  description: string;
  parking?: string;
  accessibility?: string;
  council_tax_band?: string | null;
  electricity_supply?: string;
  water_supply?: string;
  gas_supply?: string;
  broadband?: string;
  property_name?: string | null;
  remove_utility_markup?: boolean;
  enquiry_agent_id: number | null;
  property_enquiry_email: string | null;
  show_btr_room_filters: boolean;
  integration_feed_reference: string | null;
  integration_crm_property_id: string | null;
  integration_crm_branch_id: string | null;
  integration_crm_database_id: string | null;
  feed_reference_hint: string | null;
  feed_crm_property_id_hint: string | null;
  feed_crm_branch_id_hint: string | null;
  feed_crm_database_id_hint: string | null;
}

export type PropertyPerformanceGraphData = {
  categories: string[];
  series: any[];
  labels: any[];
  plotLines: any[];
};

export type PropertyListingError = Record<
  string,
  {
    title: string;
    errors: string[];
    to?: RouteLocationRaw;
  }
>;

export interface Address {
  postal_town: string;
  postcode: string;
  street: string;
  street_no: string;
}

export interface Times {
  WALKING: number;
  BICYCLING: number;
  DRIVING: number;
}

export interface Amenities {
  amenity_id: null;
  business_status: string;
  id: string;
  image_url: string;
  latitude: number;
  longitude: number;
  name: string;
  place_id: string;
  price_level: number;
  rating: number;
  user_ratings_total: number;
  types: string[];
  website: string;
  address: Address;
  addressString: string;
  distance: string;
  times: Times;
}

export type PropertiesFilters = {
  from: string | number;
  to: string | number;
  search: string;
  status: string;
  boosted: string;
  featured: string;
  bedroom: string | undefined;
  bathroom: string | undefined;
};

export type PropertiesQuery = {
  filter: PropertiesFilters;
  sort: string;
  page: number;
  perPage: number;
};

export type ActionTotals = {
  total: number;
  featureable: number;
  boostable: number;
  canBeLive: number;
  canBeNotLive: number;
};

export type SetPropertyNotLiveResponse = {
  message: string;
  count: number;
};

export type GeoLocation = {
  latitude: number | null;
  longitude: number | null;
};

export type LastUpdatedPropertyData = {
  id?: number;
  seen?: boolean;
};

export type ListingErrors = {
  id: string;
  errors: { id: string; error: string }[];
  title: string;
};

export type PropertyRooms = {
  id: number;
  property_id: number;
  number: number;
  type: string;
  price?: number;
  markup?: number;
  unit_name: string;
  unit_count?: number;
  unit_live_count?: number;
  unit_type?: string;
  unit_description?: string;
  unit_bedrooms: number;
  unit_bathrooms: number;
  unit_kitchen: string;
  unit_amenities?: string[];
  unit_security_deposit?: string;
  unit_holding_deposit?: string;
  unit_available_from: string;
  unit_available_to: string;
  unit_media?: string[];
  unit_is_live: number;
  created_at: string;
  updated_at?: string;
  deleted_at?: string;
};
